import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../styles/Navbar.css";
import LogoTwinexa from "../../assets/img/LogoTwinexa.png";
import {
  TbPhone,
  TbChevronDown,
  TbMenu2,
  TbX,
  TbBriefcase,
  TbBuildingFactory2,
  TbDeviceDesktopAnalytics,
  TbChartBar,
  TbLayoutGrid,
  TbBrandLinkedin,
  TbBrandInstagram,
  TbCpu,
  TbCode,
  TbBrain,
  TbArrowRight,
  TbSparkles,
  TbReportAnalytics,
  TbAlertTriangle,
  TbFingerprint,
  TbRoute,
  TbUsers,
  TbMessageCircle,
  TbHelp,
  TbTarget,
  TbBuildingCommunity,
} from "react-icons/tb";

/* ── Smooth scroll helper ──────────────────────────────────────── */
const scrollTo = (id, offset = 80) => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: y, behavior: "smooth" });
};

/* ── Nav data ─────────────────────────────────────────────────── */
const NAV_ITEMS = [

  /* ── 1. NOSOTROS: highlight panel ── */
  {
    label: "Nosotros",
    icon: <TbBuildingCommunity size={15} />,
    highlight: {
      label: "Quiénes somos",
      desc: "El problema que resolvemos, nuestra identidad y cómo trabajamos.",
      accent: "#7c3aed",
    },
    children: [
      {
        label: "El Problema",
        sub: "Lo que nadie resuelve del todo",
        scrollId: "problema",
        icon: <TbAlertTriangle size={18} />,
        accent: "#e11d48",
      },
      {
        label: "Nuestra Identidad",
        sub: "Por qué existimos y hacia dónde vamos",
        scrollId: "identidad",
        icon: <TbFingerprint size={18} />,
        accent: "#7c3aed",
      },
      {
        label: "Metodología",
        sub: "Cómo llevamos tu proyecto a producción",
        scrollId: "metodologia",
        icon: <TbRoute size={18} />,
        accent: "#059669",
      },
    ],
  },

  /* ── 2. ECOSISTEMA: mega-menu 2 tracks ── */
  {
    label: "Ecosistema",
    icon: <TbBriefcase size={15} />,
    mega: true,
    scrollId: "ecosistema",
    tracks: [
      {
        id: "industrial",
        label: "Soluciones Industriales",
        accent: "#7c3aed",
        items: [
          {
            label: "ERP Industrial",
            sub: "Finanzas, logística y operaciones unificadas",
            scrollId: "sol-erp",
            icon: <TbLayoutGrid size={18} />,
            accent: "#7c3aed",
          },
          {
            label: "MES",
            sub: "Control de producción en tiempo real",
            scrollId: "sol-mes",
            icon: <TbCpu size={18} />,
            accent: "#2563eb",
          },
          {
            label: "Gemelo Digital",
            sub: "Simulá tu planta antes de cambiar",
            scrollId: "sol-dt",
            icon: <TbDeviceDesktopAnalytics size={18} />,
            accent: "#0891b2",
          },
        ],
      },
      {
        id: "strategic",
        label: "Software Estratégico",
        accent: "#059669",
        items: [
          {
            label: "Desarrollo a Medida",
            sub: "Tu sistema, tus reglas, tu negocio",
            scrollId: "sol-dev",
            icon: <TbCode size={18} />,
            accent: "#059669",
          },
          {
            label: "IA para Empresas",
            sub: "Copiloto operativo conectado a tu data",
            scrollId: "sol-ai",
            icon: <TbBrain size={18} />,
            accent: "#d97706",
          },
        ],
      },
    ],
    footer: {
      label: "Ver el ecosistema completo",
      scrollId: "ecosistema",
      icon: <TbSparkles size={14} />,
    },
  },

  /* ── 3. INDUSTRIA 4.0: highlight panel ── */
  {
    label: "Industria 4.0",
    icon: <TbBuildingFactory2 size={15} />,
    highlight: {
      label: "Fábrica inteligente",
      desc: "Indicadores en tiempo real y casos reales de manufactura.",
      accent: "#2563eb",
    },
    children: [
      {
        label: "Indicadores 4.0",
        sub: "OEE, FTQ, MTBF, Scrap y más — en vivo",
        scrollId: "indicadores",
        icon: <TbChartBar size={18} />,
        accent: "#2563eb",
      },
      {
        label: "Track Record",
        sub: "Implementaciones reales en producción",
        scrollId: "clientes",
        icon: <TbReportAnalytics size={18} />,
        accent: "#059669",
      },
    ],
  },

  /* ── 4. TU EMPRESA: highlight panel ── */
  {
    label: "Tu Empresa",
    icon: <TbTarget size={15} />,
    highlight: {
      label: "¿Te identificás?",
      desc: "Descubrí si tu empresa es el perfil que Twinexa puede transformar.",
      accent: "#d97706",
    },
    children: [
      {
        label: "Tu Desafío",
        sub: "¿Qué frena el crecimiento de tu operación?",
        scrollId: "desafio",
        icon: <TbTarget size={18} />,
        accent: "#d97706",
      },
      {
        label: "Clientes",
        sub: "Empresas que ya dan el salto con Twinexa",
        scrollId: "clientes",
        icon: <TbUsers size={18} />,
        accent: "#059669",
      },
    ],
  },

  /* ── 5. CONTACTO ── */
  {
    label: "Contacto",
    scrollId: "contacto",
    icon: <TbMessageCircle size={15} />,
  },

  /* ── 6. FAQ ── */
  {
    label: "FAQ",
    scrollId: "faq",
    icon: <TbHelp size={15} />,
  },
];

/* ── Component ────────────────────────────────────────────────── */
function NavbarManagement() {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [openDrop, setOpenDrop]   = useState(null); // label of open dropdown
  const location = useLocation();
  const navRef   = useRef(null);

  /* scroll detection */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* close menu on route change */
  useEffect(() => {
    setMenuOpen(false);
    setOpenDrop(null);
  }, [location]);

  /* close dropdown when clicking outside */
  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDrop(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleDrop = (label) =>
    setOpenDrop((prev) => (prev === label ? null : label));

  return (
    <>
    <nav
      ref={navRef}
      className={`nb-root${scrolled ? " nb-scrolled" : ""}${menuOpen ? " nb-open" : ""}`}
    >
      {/* ── Shimmer bottom line ── */}
      <div className="nb-shimmer" />

      <div className="nb-inner">

        {/* Logo */}
        <button
          type="button"
          className="nb-logo"
          onClick={() => { scrollTo("inicio"); setMenuOpen(false); }}
        >
          <img src={LogoTwinexa} alt="Twinexa" />
        </button>

        {/* ── Desktop nav ── */}
        <ul className="nb-links">
          {NAV_ITEMS.map((item) => {
            if (item.mega) {
              /* ── MEGA MENU (Ecosistema) ── */
              return (
                <li
                  key={item.label}
                  className={`nb-item nb-has-drop${openDrop === item.label ? " nb-drop-open" : ""}`}
                  onMouseEnter={() => setOpenDrop(item.label)}
                  onMouseLeave={() => setOpenDrop(null)}
                >
                  <button
                    className="nb-link nb-drop-btn"
                    type="button"
                    onClick={() => { scrollTo(item.scrollId); setOpenDrop(null); }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    <TbChevronDown size={14} className="nb-chevron" />
                  </button>

                  <div className="nb-dropdown nb-dropdown--mega">
                    <div className="nb-dropdown-inner nb-mega-inner">
                      {/* Tracks grid */}
                      <div className="nb-mega-grid">
                        {item.tracks.map((track) => (
                          <div key={track.id} className="nb-mega-track">
                            <span
                              className="nb-mega-track-label"
                              style={{ "--track-accent": track.accent }}
                            >
                              {track.label}
                            </span>
                            {track.items.map((child) => (
                              <button
                                key={child.label}
                                type="button"
                                className={`nb-drop-item${child.soon ? " nb-soon" : ""}`}
                                style={{ "--drop-accent": child.accent }}
                                onClick={() => { scrollTo(child.scrollId); setOpenDrop(null); }}
                              >
                                <span className="nb-drop-icon nb-drop-icon--accent">
                                  {child.icon}
                                </span>
                                <span className="nb-drop-text">
                                  <span className="nb-drop-label">{child.label}</span>
                                  <span className="nb-drop-sub">{child.sub}</span>
                                </span>
                                {child.soon && <span className="nb-badge">Soon</span>}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>

                      {/* Footer CTA strip */}
                      {item.footer && (
                        <div className="nb-mega-footer">
                          <button
                            type="button"
                            className="nb-mega-footer-link"
                            onClick={() => { scrollTo(item.footer.scrollId); setOpenDrop(null); }}
                          >
                            {item.footer.icon}
                            <span>{item.footer.label}</span>
                            <TbArrowRight size={14} className="nb-mega-footer-arrow" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            } else if (item.children) {
              /* ── HIGHLIGHT PANEL (Industria 4.0) ── */
              return (
                <li
                  key={item.label}
                  className={`nb-item nb-has-drop${openDrop === item.label ? " nb-drop-open" : ""}`}
                  onMouseEnter={() => setOpenDrop(item.label)}
                  onMouseLeave={() => setOpenDrop(null)}
                >
                  <button className="nb-link nb-drop-btn" type="button">
                    {item.icon}
                    <span>{item.label}</span>
                    <TbChevronDown size={14} className="nb-chevron" />
                  </button>

                  <div className="nb-dropdown">
                    <div className="nb-dropdown-inner">
                      {item.highlight && (
                        <div
                          className="nb-highlight-header"
                          style={{ "--hl-accent": item.highlight.accent }}
                        >
                          <span className="nb-highlight-label">{item.highlight.label}</span>
                          <span className="nb-highlight-desc">{item.highlight.desc}</span>
                        </div>
                      )}
                      {item.children.map((child) => (
                        <button
                          key={child.label}
                          type="button"
                          className={`nb-drop-item${child.soon ? " nb-soon" : ""}`}
                          style={{ "--drop-accent": child.accent }}
                          onClick={() => { scrollTo(child.scrollId); setOpenDrop(null); }}
                        >
                          <span className="nb-drop-icon nb-drop-icon--accent">
                            {child.icon}
                          </span>
                          <span className="nb-drop-text">
                            <span className="nb-drop-label">{child.label}</span>
                            <span className="nb-drop-sub">{child.sub}</span>
                          </span>
                          {child.soon && <span className="nb-badge">Soon</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                </li>
              );
            } else {
              /* ── SIMPLE SCROLL LINK ── */
              return (
                <li key={item.label} className="nb-item">
                  <button
                    type="button"
                    className="nb-link"
                    onClick={() => scrollTo(item.scrollId)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            }
          })}
        </ul>

        {/* CTA */}
        <a
          href="https://calendly.com/twinexa-software/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="nb-cta"
        >
          <TbPhone size={15} />
          <span>Agenda una reunión</span>
        </a>

        {/* Hamburger */}
        <button
          className="nb-hamburger"
          onClick={() => setMenuOpen((p) => !p)}
          aria-label="Toggle menu"
          type="button"
        >
          {menuOpen ? <TbX size={24} /> : <TbMenu2 size={24} />}
        </button>
      </div>

    </nav>

      {/* ── Mobile drawer ── */}
      <div className={`nb-drawer${menuOpen ? " nb-drawer-open" : ""}`}>
        <div className="nb-drawer-scroll">
        <ul className="nb-drawer-list">
          {NAV_ITEMS.map((item) => {
            const hasChildren = item.mega
              ? item.tracks.flatMap((t) => t.items)
              : item.children;

            if (hasChildren) {
              const allChildren = item.mega
                ? item.tracks.flatMap((t) => t.items)
                : item.children;

              return (
                <li key={item.label} className="nb-drawer-item">
                  <button
                    className="nb-drawer-link nb-drawer-drop-btn"
                    type="button"
                    onClick={() => toggleDrop(item.label)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    <TbChevronDown
                      size={14}
                      className={`nb-chevron${openDrop === item.label ? " nb-chevron-open" : ""}`}
                    />
                  </button>
                  <ul className={`nb-drawer-sub${openDrop === item.label ? " nb-drawer-sub-open" : ""}`}>
                    {allChildren.map((child) => (
                      <li key={child.label}>
                        <button
                          type="button"
                          className={`nb-drawer-sub-link${child.soon ? " nb-soon" : ""}`}
                          onClick={() => { scrollTo(child.scrollId); setMenuOpen(false); }}
                        >
                          {child.icon}
                          <span>{child.label}</span>
                          {child.soon && <span className="nb-badge">Soon</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            } else {
              return (
                <li key={item.label} className="nb-drawer-item">
                  <button
                    type="button"
                    className="nb-drawer-link"
                    onClick={() => { scrollTo(item.scrollId); setMenuOpen(false); }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            }
          })}
        </ul>

        {/* Mobile social + CTA */}
        <div className="nb-drawer-footer">
          <div className="nb-drawer-social">
            <a
              href="https://www.linkedin.com/company/twinexa"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="nb-social-btn"
            >
              <TbBrandLinkedin size={18} />
            </a>
            <a
              href="https://www.instagram.com/twinexaa/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="nb-social-btn"
            >
              <TbBrandInstagram size={18} />
            </a>
          </div>
          <a
            href="https://calendly.com/twinexa-software/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="nb-cta nb-cta-mobile"
          >
            <TbPhone size={15} />
            <span>Agenda una reunión</span>
          </a>
        </div>
        </div>{/* end nb-drawer-scroll */}
      </div>
    </>
  );
}

export default NavbarManagement;
